import { useCallback, useEffect, useRef, useState } from "react";
import { initialState, handleKey, type VimState } from "../shell/vim";

export interface Line {
  text: string;
  className?: string;
  href?: string;
}

interface TerminalLineProps extends Line {
  isCursor: boolean;
  cursorCharIndex: number;
}

function TerminalLine({ text, className, href, isCursor, cursorCharIndex }: TerminalLineProps) {
  const glow = { textShadow: "var(--term-glow, none)" };
  const cls = className ?? "text-term-primary";

  const renderContent = (children: React.ReactNode) =>
    href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} style={{ ...glow, textDecoration: "underline", color: "inherit" }}>
        {children}
      </a>
    ) : (
      children
    );

  if (!isCursor) {
    return (
      <div className={cls} style={glow}>
        {renderContent(text)}
      </div>
    );
  }

  const pos = Math.min(cursorCharIndex, Math.max(text.length - 1, 0));
  const before = text.slice(0, pos);
  const ch = text[pos] ?? "\u00a0";
  const after = text.slice(pos + 1);

  return (
    <div className={cls} style={glow}>
      {renderContent(
        <>
          {before}
          <span style={{ backgroundColor: "var(--term-text)", color: "var(--term-bg)" }}>
            {ch}
          </span>
          {after}
        </>
      )}
    </div>
  );
}

interface TerminalProps {
  lines: Line[];
}

export function Terminal({ lines }: TerminalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const [vim, setVim] = useState<VimState>(() => initialState(lines.length));

  useEffect(() => {
    setVim((v) => ({ ...v, lineCount: lines.length }));
  }, [lines.length]);

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  useEffect(() => {
    cursorRef.current?.scrollIntoView({ block: "nearest" });
  }, [vim.cursor]);

  const lineTexts = lines.map((l) => l.text);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.ctrlKey && e.key === "]") {
        const href = lines[vim.cursor]?.href;
        if (href) {
          e.preventDefault();
          window.open(href, "_blank", "noopener,noreferrer");
        }
        return;
      }
      if (consumedByVim(e.key, e.ctrlKey, vim.mode)) e.preventDefault();
      setVim((v) => handleKey(v, e.key, e.ctrlKey, lineTexts));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vim.mode, vim.cursor, lineTexts.join("\n")]
  );

  const statusLeft =
    vim.mode === "command" ? `:${vim.commandInput}` : `-- NORMAL --`;
  const statusRight = `${vim.cursor + 1}/${lines.length}`;

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      className="bg-term font-term h-screen w-full flex flex-col overflow-hidden outline-none"
    >
      <div data-testid="output" className="flex-1 overflow-y-auto w-full">
        <div className="term-content px-4 py-4 break-words">
          {lines.map((line, i) => (
            <div key={i} ref={i === vim.cursor ? cursorRef : undefined}>
              <TerminalLine
                {...line}
                isCursor={i === vim.cursor}
                cursorCharIndex={vim.charIndex}
              />
            </div>
          ))}
        </div>
      </div>

      <div
        className="font-term term-content shrink-0 flex justify-between px-4 py-1 text-term-dim"
        style={{ borderTop: "1px solid var(--term-dim)" }}
      >
        <span className="text-term-bright">{statusLeft}</span>
        <span>{statusRight}</span>
      </div>
    </div>
  );
}

function consumedByVim(key: string, ctrlKey: boolean, mode: VimState["mode"]): boolean {
  if (mode === "command") {
    return ["Escape", "Enter", "Backspace"].includes(key) || key.length === 1;
  }
  if (ctrlKey && (key === "d" || key === "u" || key === "]")) return true;
  return ["j", "k", "g", "G", "w", "b", "h", "l", "$", ":", "Escape"].includes(key);
}
